import { normalizeScan, type CodeSuggestion, type OrderStatus } from '@wms/domain';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { errorMessage, floorAction, sessionRefused, type FloorAction } from '../api';
import { feedback } from '../feedback';
import { asked, buttonWords, resentNotice, SCAN_WORDS, scansLeftText, type ActionWords, type Notice } from '../floor';
import { channelText, initials, statusLabel, statusTone, swatch } from '../format';
import { initialLoad, settle, staleText, type LoadState, type Stale } from '../loading';
import { createScanRecorder, describeScan, judgeScan, logRefusal, type ScanVerdict } from '../scan';
import { useOptionalSession } from '../session';
import { typedByPerson, type SuggestSource } from '../suggest';
import { CameraScanner, cameraSupported } from './camera';
import { Icon } from './icons';
import { SuggestList, useCodeSuggestions } from './suggest';

export { SuggestList, useCodeSuggestions } from './suggest';

// --- toasts ----------------------------------------------------------------------------
type Tone = 'ok' | 'bad' | 'info';
interface Toast {
  readonly id: number;
  readonly text: string;
  readonly tone: Tone;
}
const ToastContext = createContext<(text: string, tone?: Tone) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const push = useCallback((text: string, tone: Tone = 'info') => {
    const id = next.current++;
    setToasts((all) => [...all.slice(-3), { id, text, tone }]);
    setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), tone === 'bad' ? 6000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone === 'info' ? '' : t.tone}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// --- data loading ------------------------------------------------------------------------
export interface Loaded<T> {
  readonly data: T | undefined;
  /** the latest load failed with nothing current to show (the first load, or new deps) */
  readonly error: string | null;
  /** a refresh failed: data is still the last good load, and the screen's strip says how old (StaleScope) */
  readonly stale: Stale | null;
  readonly loading: boolean;
  reload(): Promise<void>;
}

/** Loads on mount and when deps change; refreshMs keeps a list current on a shared screen. */
export function useLoad<T>(load: () => Promise<T>, deps: readonly unknown[], refreshMs?: number): Loaded<T> {
  const [state, setState] = useState<LoadState<T>>(initialLoad);
  const [loading, setLoading] = useState(true);
  const loader = useRef(load);
  loader.current = load;
  const generation = useRef(0);
  // which deps a load was for: only data of the same deps is kept through a failed refresh
  const epoch = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    const at = epoch.current;
    setLoading(true);
    try {
      const value = await loader.current();
      if (mine === generation.current) setState((s) => settle(s, { epoch: at, at: Date.now(), ok: true, value }));
    } catch (e) {
      if (mine === generation.current) setState((s) => settle(s, { epoch: at, at: Date.now(), ok: false, error: e }));
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    epoch.current += 1;
    void reload();
    if (!refreshMs) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, refreshMs);
    return () => clearInterval(timer);
  }, deps);

  useStaleReport(state.stale, reload);

  return { data: state.data, error: state.error, stale: state.stale, loading, reload };
}

/**
 * A screen that refreshes on its own (not through useLoad) says here that its latest refresh failed:
 * the strip shows how old the screen is, and "Tekrar dene" calls `reload` (keep it stable).
 */
export function useStaleReport(stale: Stale | null, reload: () => Promise<void>) {
  const report = useContext(StaleContext);
  const id = useId();
  useEffect(() => {
    if (!report || !stale) return;
    report(id, { ...stale, reload });
    return () => report(id, null);
  }, [report, id, stale, reload]);
}

// --- a weak connection ---------------------------------------------------------------------
interface StaleEntry extends Stale {
  reload(): Promise<void>;
}
const StaleContext = createContext<((id: string, entry: StaleEntry | null) => void) | null>(null);

/**
 * Around a screen: a list whose refresh failed stays on screen under a thin strip that says how old
 * it is. The strip goes once a refresh comes through (the screen's own timer, the phone back
 * online, or "Tekrar dene").
 */
export function StaleScope({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ReadonlyMap<string, StaleEntry>>(() => new Map());
  const report = useCallback((id: string, entry: StaleEntry | null) => {
    setEntries((all) => {
      if (!entry && !all.has(id)) return all;
      const next = new Map(all);
      if (entry) next.set(id, entry);
      else next.delete(id);
      return next;
    });
  }, []);
  return (
    <StaleContext.Provider value={report}>
      {entries.size > 0 && <StaleStrip entries={[...entries.values()]} />}
      {children}
    </StaleContext.Provider>
  );
}

function StaleStrip({ entries }: { entries: readonly StaleEntry[] }) {
  const [now, setNow] = useState(Date.now);
  const retry = () => entries.forEach((e) => void e.reload());
  const latest = useRef(retry);
  latest.current = retry;
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 15_000);
    const online = () => latest.current();
    window.addEventListener('online', online);
    return () => {
      clearInterval(tick);
      window.removeEventListener('online', online);
    };
  }, []);
  const oldest = Math.min(...entries.map((e) => e.since));
  // an answer from the server (not the network) is said as it is
  const refused = entries.find((e) => !e.offline);
  return (
    <div className="stale-strip" role="status">
      <Icon name="alert" size={16} />
      <span className="stale-text">
        {staleText({ offline: !refused, since: oldest }, Math.max(now, oldest))}
        {refused && <small>{refused.error}</small>}
      </span>
      <button className="btn sm ghost" onClick={retry}>
        Tekrar dene
      </button>
    </div>
  );
}

// --- small pieces ---------------------------------------------------------------------------
export function Spinner() {
  return (
    <div className="center">
      <div className="spinner" aria-label="Yükleniyor" />
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="banner bad" role="alert">
      <Icon name="alert" />
      <div style={{ flex: 1 }}>{message}</div>
      {onRetry && (
        <button className="btn sm" onClick={onRetry}>
          Tekrar dene
        </button>
      )}
    </div>
  );
}

export const StatusChip = ({ status }: { status: OrderStatus }) => <span className={`chip ${statusTone(status)}`}>{statusLabel(status)}</span>;

/** Seller code: the floor serves every seller, so the code is on every item */
export const SellerChip = ({ code }: { code: string }) => <span className="chip seller">{code}</span>;

/**
 * The marketplace an order came from, beside its number: the full name where there is room ("Trendyol"),
 * the short code where room is tight (short: "TY"); the full name is always there on hover and read aloud.
 * count: how many orders of a route came from it ("TY ×3").
 */
export function ChannelBadge({ channel, short = false, count }: { channel: string; short?: boolean; count?: number }) {
  const { name, short: code } = channelText(channel);
  const shown = short ? code : name;
  const many = count !== undefined && count > 1;
  const full = many ? `${name}: ${count} sipariş` : name;
  return (
    <span className="chip market" title={full} aria-label={full} role="img">
      {many ? `${shown} ×${count}` : shown}
    </span>
  );
}

export function ProductThumb({ name, imageUrl, small }: { name: string; imageUrl?: string | null; small?: boolean }) {
  return (
    <div className={`thumb${small ? ' sm' : ''}`} style={{ background: imageUrl ? 'var(--surface-2)' : swatch(name) }} aria-hidden>
      {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : initials(name)}
    </div>
  );
}

export function Modal(props: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const close = useRef(props.onClose);
  close.current = props.onClose;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // at the page's root: a parent with a blur or a transform (the top bar) would hold a fixed layer inside itself
  return createPortal(
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}>
      <div className={`modal${props.wide ? ' wide' : ''}`} role="dialog" aria-modal aria-label={props.title}>
        <div className="card-h">
          <h2>{props.title}</h2>
          <span className="spacer" />
          <button className="icon-btn" onClick={props.onClose} aria-label="Kapat">
            <Icon name="x" />
          </button>
        </div>
        <div className="card-b">{props.children}</div>
        {props.footer && <div className="foot">{props.footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// --- floor actions whose answer was lost ------------------------------------------------------
export { SCAN_WORDS, type ActionWords };

/** A lost answer is asked for again this often by itself (and at once when the phone is back online). */
const AUTO_RESEND_MS = 5_000;

const upperFirst = (text: string) => text.charAt(0).toLocaleUpperCase('tr-TR') + text.slice(1);

/** The screen closed while an answer was still lost: said where the next screen shows it. */
function useLostOnLeave(lostWords: () => string | null) {
  const toast = useToast();
  const latest = useRef(lostWords);
  latest.current = lostWords;
  useEffect(
    () => () => {
      const text = latest.current();
      if (text) toast(text, 'bad');
    },
    [toast],
  );
}

/** Sends a lost action again by itself while `active`: when the phone is back online, and every few seconds. */
function useAutoResend(active: boolean, resend: () => void) {
  const latest = useRef(resend);
  latest.current = resend;
  useEffect(() => {
    if (!active) return;
    const again = () => document.visibilityState === 'visible' && latest.current();
    window.addEventListener('online', again);
    const timer = setInterval(again, AUTO_RESEND_MS);
    return () => {
      window.removeEventListener('online', again);
      clearInterval(timer);
    };
  }, [active]);
}

function LostBanner(props: { words: ActionWords; queued?: number; sending: boolean; onResend: () => void; onDrop: () => void }) {
  return (
    <div className="banner warn lost" role="alert">
      <Icon name="alert" />
      <div className="lost-body">
        <b>Bağlantı koptu — {asked(props.words)} henüz belli değil.</b>
        <span>
          Bağlantı gelince kendiliğinden tekrar gönderilir; iki kez işlenmez.
          {props.queued ? ` ${props.queued} okutma sırada.` : ''}
        </span>
        <div className="lost-actions">
          <button className="btn primary" type="button" onClick={props.onResend} disabled={props.sending}>
            <Icon name="refresh" /> {props.sending ? 'Gönderiliyor…' : 'Tekrar gönder'}
          </button>
          <button className="btn ghost" type="button" onClick={props.onDrop} disabled={props.sending}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
}

const NoticeBanner = ({ notice }: { notice: Notice }) => (
  <div className={`banner ${notice.tone}`} role="status">
    <Icon name={notice.tone === 'ok' ? 'check' : 'alert'} />
    <div>{notice.text}</div>
  </div>
);

interface LostAction {
  readonly words: ActionWords;
  readonly action: FloorAction;
  readonly send: (action: FloorAction) => Promise<unknown>;
}

export interface FloorActionRunner {
  /**
   * Runs one press of a button (its label: "Paketlemeyi tamamla") or a scan outside the scan field (SCAN_WORDS)
   * as a new FloorAction; `send` passes it to api() and reports its own errors. When its answer is lost it
   * waits in `banner` to be sent again. False: not sent, an earlier action is still with the server or waits.
   */
  run(what: string | ActionWords, send: (action: FloorAction) => Promise<unknown>): Promise<boolean>;
  /** "Bağlantı koptu…" with "Tekrar gönder", or what the repeat found; null otherwise */
  readonly banner: ReactNode;
  /** an action waits to be sent again: the screen's other buttons wait too */
  readonly waiting: boolean;
}

export interface FloorActionOptions {
  /**
   * The person gave up on a lost action ("Vazgeç"): whether it counted is not known. The screen
   * fetches what the server has before it offers that action again.
   */
  readonly onUnsure?: () => void;
}

/**
 * A button's action on the floor whose answer may be lost on a dropping connection: it is sent again
 * with the same Idempotency-Key (so it counts once) and the person is told whether it counted.
 * A dialog's action runs on its screen's runner: closing the dialog must not lose the key.
 */
export function useFloorAction(options: FloorActionOptions = {}): FloorActionRunner {
  const unsure = useRef(options.onUnsure);
  unsure.current = options.onUnsure;
  const [lost, setLost] = useState<LostAction | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // one action at a time: a second tap does not send a second action
  const busy = useRef(false);
  const held = useRef<LostAction | null>(null);
  const hold = (pending: LostAction | null) => {
    held.current = pending;
    setLost(pending);
  };

  const attempt = async (pending: LostAction, again: boolean) => {
    busy.current = true;
    setSending(again);
    // a try that sends nothing (the screen moved on) leaves no outcome
    pending.action.outcome = null;
    try {
      await pending.send(pending.action);
    } catch {
      // the screen reports its own errors
    } finally {
      busy.current = false;
      setSending(false);
    }
    if (pending.action.outcome === 'lost') return hold(pending);
    hold(null);
    if (again) setNotice(resentNotice(pending.words, pending.action));
  };

  const resend = () => {
    if (held.current && !busy.current) void attempt(held.current, true);
  };
  useAutoResend(lost !== null, resend);
  // a dialog closed or the screen left while the answer is still lost (a repeat's finished one is not)
  useLostOnLeave(() => {
    const pending = held.current;
    if (!pending || pending.action.outcome === 'counted' || pending.action.outcome === 'refused') return null;
    return `Bağlantı koptu — ${asked(pending.words)} belli değil. Ekrana dönüp kontrol edin.`;
  });

  return {
    run: async (what, send) => {
      if (held.current || busy.current) return false;
      setNotice(null);
      await attempt({ words: typeof what === 'string' ? buttonWords(what) : what, action: floorAction(), send }, false);
      return true;
    },
    banner: lost ? (
      <LostBanner
        words={lost.words}
        sending={sending}
        onResend={resend}
        onDrop={() => {
          if (busy.current) return;
          hold(null);
          setNotice({ tone: 'bad', text: `${upperFirst(asked(lost.words))} belli değil: ekranı yenileyip kontrol edin.` });
          unsure.current?.();
        }}
      />
    ) : notice ? (
      <NoticeBanner notice={notice} />
    ) : null,
    waiting: lost !== null,
  };
}

// --- the scan field --------------------------------------------------------------------------
export interface ScanFieldProps {
  readonly placeholder: string;
  /**
   * One scan, sent as `action` (pass it to api()): a scan whose answer was lost waits here and is sent
   * again with the same Idempotency-Key, so it counts once.
   */
  readonly onScan: (code: string, action: FloorAction) => void | Promise<void>;
  readonly hint?: ReactNode;
  readonly disabled?: boolean;
  /** Keep the cursor in the field so a handheld scanner always types into it */
  readonly keepFocus?: boolean;
  readonly label?: string;
  /**
   * Anyone may type here (it opens a record and changes no stock). Elsewhere the warehouse staff
   * read codes with a barcode reader or the camera; supervisors may type them.
   */
  readonly allowTyping?: boolean;
  /**
   * A scan's answer was lost and it waits to be sent again (true), or no longer (false). A "geri al"
   * waits meanwhile: it would take back the scan before it, the lost one counting after all.
   */
  readonly onHeld?: (held: boolean) => void;
  /**
   * How far the page moves when the field takes the cursor while out of view: 'center' (the browser's
   * way: the field mid-screen) or 'nearest' (only as far as the field needs, so what stands above it,
   * e.g. the walk's labels card, stays in view).
   */
  readonly reveal?: 'center' | 'nearest';
  /**
   * With 'nearest': what stands above the field and wins when the two do not fit on the screen
   * together (the page then shows its top, the field under it may stay below: a reader still types
   * into it), e.g. the walk's labels card.
   */
  readonly revealAbove?: () => Element | null;
  /**
   * The screen moved on (the walk's next stop): when this changes, the field comes back into view as
   * when it takes the cursor (revealAbove still winning), also while it keeps the cursor.
   */
  readonly revealKey?: string | number;
  /**
   * What a manager typing a code here is offered (bins, products, orders, shipments): a list under
   * the field once the typing pauses; a pick sends that code as a scan would. Never for a reader's
   * burst, never for the warehouse staff.
   */
  readonly suggest?: SuggestSource;
}

/** A scan waiting to be sent; `run` is the screen's handler it was first sent with. */
interface QueuedScan {
  readonly code: string;
  readonly action: FloorAction;
  run?: ScanFieldProps['onScan'];
  /** being sent again after a lost answer (a first try may make two requests: its tries do not tell) */
  resending?: boolean;
}

/** Said when a staff member types a code the floor reads with a barcode reader. */
export const TYPED_CODE_REFUSED = 'Kod elle yazılamaz: okuyucuyla ya da kamerayla okutun. Barkod okunmuyorsa vardiya sorumlusuna haber verin.';

/**
 * Under a refusal: how the code came in (counts and timing, never the code), folded away, for the
 * supervisor who is called over when a real reader is refused. Also left in the browser console.
 */
export function ScanWhy({ verdict }: { verdict: ScanVerdict }) {
  return (
    <details className="scan-why">
      <summary>Ayrıntı (vardiya sorumlusu için)</summary>
      {describeScan(verdict)}
      <br />
      Gerçek bir okuyucu reddediliyorsa ayarlarına bakın: sonuna Enter, gönderme hızı en yüksek, klavye dili cihazla aynı.
    </details>
  );
}

/** Whether this person must read codes with a barcode reader: the warehouse staff (supervisors may type them). */
export function useScannerOnly(): boolean {
  const session = useOptionalSession();
  return Boolean(session?.isOperator && !session.isSupervisor);
}

/**
 * Where scanners type: Enter submits, the value is normalised (Turkish-Q: ı/İ → I,
 * spaces removed, upper case). The camera button reads barcodes with the phone camera.
 * Scans go to the server one at a time; one whose answer was lost holds the others until it is
 * sent again ("Bağlantı koptu — son okutma SAYILDI / SAYILMADI").
 */
export function ScanField(props: ScanFieldProps) {
  const { placeholder, onScan, hint, disabled, keepFocus = true, label, allowTyping = false, onHeld, reveal = 'center', revealAbove, revealKey, suggest } = props;
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  // the field into view: 'nearest' moves the page only as far as the field needs
  const showField = () => {
    if (reveal === 'center') return box.current?.scrollIntoView({ block: 'center' });
    box.current?.scrollIntoView({ block: 'nearest' });
    // nothing moves when it is in view; pushed out by the field, it comes back to the top
    revealAbove?.()?.scrollIntoView({ block: 'nearest' });
  };
  // the cursor back into the field; a field that has it already moves nothing (as the browser's focus()).
  // show: 'nearest' brings it into view; false takes the scanner back where the page is
  const focusField = (show = true) => {
    const field = input.current;
    if (!field || document.activeElement === field) return;
    if (reveal === 'center') return field.focus();
    field.focus({ preventScroll: true });
    if (show) showField();
  };
  const id = useId();
  const [value, setValue] = useState('');
  const [camera, setCamera] = useState(false);
  // how the text got here: a staff member's code must come from a reader
  const scannerOnly = useScannerOnly() && !allowTyping;
  const trace = useRef(createScanRecorder());
  const [refused, setRefused] = useState<ScanVerdict | null>(null);
  // scans that arrive while the previous one is still with the server wait their turn
  const handler = useRef(onScan);
  handler.current = onScan;
  const queue = useRef<QueuedScan[]>([]);
  const draining = useRef(false);
  // the scan at the head whose answer was lost: the queue waits for it to be sent again
  const held = useRef<QueuedScan | null>(null);
  const [lost, setLost] = useState<QueuedScan | null>(null);
  const [sending, setSending] = useState<{ queued: number } | null>(null);
  const [slow, setSlow] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // scans that did not count because the session ended under them (the screen is about to go)
  const stopped = useRef(0);
  const heldNow = lost !== null;
  const heldTo = useRef(onHeld);
  heldTo.current = onHeld;
  useEffect(() => {
    if (!heldNow) return;
    heldTo.current?.(true);
    return () => heldTo.current?.(false);
  }, [heldNow]);

  useEffect(() => {
    if (!disabled && !camera) focusField();
  }, [disabled, camera]);

  // the screen moved on under a field that kept the cursor (a reader's scans): focusField moves nothing
  // then, and the field with what it reports under it could be left below the screen
  const keyShown = useRef(revealKey);
  useEffect(() => {
    if (keyShown.current === revealKey) return;
    keyShown.current = revealKey;
    if (disabled || camera || document.querySelector('.modal-back, .camera')) return;
    showField();
  }, [revealKey]);

  // a quick answer needs no "Gönderiliyor…"
  const busy = sending !== null;
  useEffect(() => {
    if (!busy) return setSlow(false);
    const timer = setTimeout(() => setSlow(true), 600);
    return () => clearTimeout(timer);
  }, [busy]);

  const drain = async () => {
    if (draining.current || held.current) return;
    draining.current = true;
    try {
      while (queue.current.length > 0) {
        const scan = queue.current[0]!;
        const again = scan.run !== undefined;
        scan.resending = again;
        setSending({ queued: queue.current.length - 1 });
        // a repeat goes as the first try went: the same request with the same key
        scan.run ??= handler.current;
        scan.action.outcome = null;
        try {
          await scan.run(scan.code, scan.action);
        } catch {
          // the screen reports its own errors
        }
        if (scan.action.outcome === 'lost') {
          held.current = scan;
          setLost(scan);
          break;
        }
        queue.current.shift();
        if (again) {
          setLost(null);
          setNotice(resentNotice(SCAN_WORDS, scan.action));
        }
        // signed out (or a new password first): the scans behind would go with no session, one refusal each
        if (sessionRefused(scan.action)) {
          stopped.current = queue.current.length + 1;
          queue.current = [];
          setNotice({ tone: 'bad', text: scansLeftText({ unknown: false, notCounted: stopped.current })! });
          break;
        }
        // let the screen re-render, so the next scan sees the new step
        await new Promise((resolve) => setTimeout(resolve, 16));
      }
    } finally {
      draining.current = false;
      setSending(null);
      // a dialog the scan opened keeps the cursor (the scanner types into it); a field inside one takes it
      const dialog = document.querySelector('.modal-back, .camera');
      if (keepFocus && (!dialog || dialog.contains(input.current))) focusField();
    }
  };

  const resend = () => {
    if (!held.current || draining.current) return;
    held.current = null;
    void drain();
  };
  useAutoResend(lost !== null, resend);
  // the screen left while a scan's answer was lost (held, or being sent again): its queue goes with it
  useLostOnLeave(() => {
    const head = queue.current[0];
    const unknown =
      head !== undefined && (held.current === head || (head.resending === true && head.action.outcome !== 'counted' && head.action.outcome !== 'refused'));
    return scansLeftText(unknown ? { unknown, notCounted: queue.current.length - 1 } : { unknown, notCounted: stopped.current });
  });

  // given up: nothing more is sent; whether the lost scan counted is not known
  const drop = () => {
    if (draining.current) return;
    const unsent = queue.current.length - 1;
    queue.current = [];
    held.current = null;
    setLost(null);
    setNotice({
      tone: 'bad',
      text: `Son okutma sayıldı mı belli değil: ekranı yenileyip adede bakın, eksikse tekrar okutun.${unsent > 0 ? ` Sıradaki ${unsent} okutma gönderilmedi.` : ''}`,
    });
    focusField();
  };

  // the code as read: a reader on another keyboard layout is set right by the look-ups, after the code
  // itself failed (a code may hold a '+', '*' or '=' of its own)
  const submit = (raw: string) => {
    const code = normalizeScan(raw);
    setValue('');
    trace.current.clear();
    setRefused(null);
    if (!code) return;
    if (!held.current) setNotice(null);
    stopped.current = 0;
    queue.current.push({ code, action: floorAction() });
    // "N okutma sırada" counts the scans behind the one with the server (or the lost one)
    if (held.current) setLost({ ...held.current });
    else if (draining.current) setSending({ queued: queue.current.length - 1 });
    void drain();
  };
  const queued = queue.current.length - 1;
  const typed = () => {
    const verdict = judgeScan(trace.current.trace(), value);
    if (!scannerOnly || verdict.reader) return submit(value);
    setValue('');
    trace.current.clear();
    setRefused(verdict);
    logRefusal(verdict);
    feedback('error');
  };
  // a manager typing a code is offered what it may mean; a reader's code never opens the list
  const suggestions = useCodeSuggestions({
    source: suggest,
    enabled: !scannerOnly && !disabled && !camera,
    value,
    byPerson: () => typedByPerson(trace.current.trace(), value),
  });
  const pickSuggestion = (s: CodeSuggestion) => {
    suggestions.close();
    submit(s.code);
  };

  return (
    <div className="scan-field" ref={box}>
      {label && (
        <div className="field" style={{ marginBottom: 6 }}>
          <label htmlFor={id}>{label}</label>
        </div>
      )}
      <div className="scan">
        <span className="scan-box">
          <input
            id={id}
            ref={input}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            enterKeyHint="go"
            {...suggestions.inputProps}
            onChange={(e) => {
              // only what went into the field since it was last empty counts
              trace.current.change(e);
              setValue(e.target.value);
            }}
            onKeyDown={(e) => {
              trace.current.key(e);
              // the open list's keys: the arrows, Enter on a highlighted row, Escape
              if (suggestions.onKey(e, pickSuggestion)) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                typed();
              }
            }}
            onBlur={() => {
              if (suggestions.open) suggestions.close();
              // a tap elsewhere (a button) gets its click first; then the field takes the scanner back, also
              // from a button: a reader's Enter would press that button again (a second "geri al", a reprint)
              if (!keepFocus || disabled) return;
              setTimeout(() => {
                if (document.querySelector('.modal-back, .camera')) return;
                const active = document.activeElement;
                const pressable = active === document.body || active === null || (active instanceof HTMLElement && active.matches('button, a, [role="button"], .chip'));
                // 'nearest': the page stays where the tap left it (a card that unfolded above is read, not jumped past)
                if (pressable) focusField(false);
              }, 250);
            }}
          />
          {/* a phone's narrow field: the placeholder wraps onto two lines instead of being cut */}
          {placeholder && !value && (
            <span className="scan-ph" aria-hidden="true">
              {placeholder}
            </span>
          )}
        </span>
        {cameraSupported() && (
          <button className="icon-btn" type="button" onClick={() => setCamera(true)} aria-label="Kamera ile okut" disabled={disabled}>
            <Icon name="camera" />
          </button>
        )}
      </div>
      <SuggestList suggestions={suggestions} onPick={pickSuggestion} />
      {refused ? (
        <div className="scan-hint bad" role="alert">
          {TYPED_CODE_REFUSED}
          <ScanWhy verdict={refused} />
        </div>
      ) : sending && !lost && (slow || sending.queued > 0) ? (
        <div className="scan-hint sending" role="status">
          Gönderiliyor…{sending.queued > 0 ? ` (${sending.queued} okutma sırada)` : ''}
        </div>
      ) : (
        hint && <div className="scan-hint">{hint}</div>
      )}
      {lost ? (
        <LostBanner words={SCAN_WORDS} queued={queued} sending={busy} onResend={resend} onDrop={drop} />
      ) : (
        notice && <NoticeBanner notice={notice} />
      )}
      {/* on the page itself: a sticky box around the field (mal kabul) would put it under the tab bar */}
      {camera &&
        createPortal(
          <CameraScanner
            onClose={() => setCamera(false)}
            onResult={(code) => {
              setCamera(false);
              submit(code);
            }}
          />,
          document.body,
        )}
    </div>
  );
}
